import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Thermometer, X } from 'lucide-react';
import { AUDIENCES, type AudienceMeta } from '@shared/constants/audiences';
import { useAudienceStore } from '../../stores/audience';
import { storeLocation } from '../../lib/audience-storage';
import {
  getZoning,
  CLIMATE_ZONE_LABELS,
  CLIMATE_ZONE_COLORS,
  ANAH_ZONE_LABELS,
} from '../../lib/zoning';
import { Button } from '../ui/button';
import { CitySelect, type CommuneResult } from '../ui/city-select';
import { AUDIENCE_ICON_MAP } from '../../lib/audience-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';
import { useConsent } from '../../stores/consent';

const TONE_BG: Record<AudienceMeta['tone'], string> = {
  brand: 'bg-brand-50 group-hover:bg-brand-100',
  lavender: 'bg-lavender-50 group-hover:bg-lavender-100',
  sage: 'bg-sage-100 group-hover:bg-sage-100',
  'lavender-700': 'bg-lavender-50 group-hover:bg-lavender-100',
  'brand-700': 'bg-brand-50 group-hover:bg-brand-100',
  accent: 'bg-accent-50 group-hover:bg-accent-100',
};

const TONE_TEXT: Record<AudienceMeta['tone'], string> = {
  brand: 'text-brand-700',
  lavender: 'text-lavender-600',
  sage: 'text-sage-500',
  'lavender-700': 'text-lavender-700',
  'brand-700': 'text-brand-700',
  accent: 'text-accent-700',
};

const TONE_RING: Record<AudienceMeta['tone'], string> = {
  brand: 'ring-brand-300/60',
  lavender: 'ring-lavender-300/60',
  sage: 'ring-sage-300/60',
  'lavender-700': 'ring-lavender-400/60',
  'brand-700': 'ring-brand-400/60',
  accent: 'ring-accent-300/60',
};

type WizardStep = 'profil' | 'commune' | 'done';

export function AudienceModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>('profil');
  const [selected, setSelected] = useState<AudienceMeta | null>(null);
  const [commune, setCommune] = useState<CommuneResult | null>(null);
  const hasRespondedToCookies = useConsent((state) => state.hasResponded);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Sélecteurs Zustand. On lit le state d'hydratation pour éviter d'ouvrir
  // la modale avant que le store ait hydraté localStorage (sinon flash).
  const modalSeen = useAudienceStore((s) => s.modalSeen);
  const hydrated = useAudienceStore((s) => s.hydrated);
  const setAudience = useAudienceStore((s) => s.setAudience);
  const markModalSeen = useAudienceStore((s) => s.markModalSeen);
  const hydrate = useAudienceStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!hydrated) return;
    if (modalSeen) return;
    if (!hasRespondedToCookies) return;
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [hasRespondedToCookies, hydrated, modalSeen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    // Focus initial sur le bouton fermer (pattern dialog accessible).
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    markModalSeen();
    setOpen(false);
    setStep('profil');
    setSelected(null);
    setCommune(null);
  };

  // On NE STOCKE PAS encore l'audience au step 'profil' : l'utilisateur peut
  // reculer pour changer de profil sans qu'on ait déjà figé son choix dans le
  // cookie/localStorage. Le profil est validé à la confirmation finale, où l'on
  // navigue vers la page de l'audience pour aligner toute l'UI (URL = vérité).
  const handleSelectAudience = (a: AudienceMeta) => {
    setSelected(a);
    setStep('commune');
  };

  const handleConfirmCommune = () => {
    if (commune) storeLocation(commune);
    if (selected) setAudience(selected.id);
    markModalSeen();
    setStep('done');
  };

  const handleSkipCommune = () => {
    if (selected) setAudience(selected.id);
    markModalSeen();
    setStep('done');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="audience-modal-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: easeStripe }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
        >
          <button
            type="button"
            aria-label="Fermer"
            onClick={handleClose}
            className="absolute inset-0 cursor-default bg-ink/40"
          />

          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.25, ease: easeStripe }}
            className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-stone-200/70 bg-paper shadow-floating"
          >
            <div className="flex items-start justify-between gap-4 border-b border-stone-200/70 px-6 py-5">
              <div className="flex items-center gap-3">
                {step === 'commune' && (
                  <button
                    type="button"
                    onClick={() => setStep('profil')}
                    aria-label="Retour au choix du profil"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-paper text-stone-500 transition-colors hover:bg-stone-50 hover:text-ink"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                    {step === 'profil' && 'Étape 1 sur 2 — Profil'}
                    {step === 'commune' && 'Étape 2 sur 2 — Votre commune'}
                    {step === 'done' && 'Personnalisation activée'}
                  </p>
                  <h2
                    id="audience-modal-title"
                    className="mt-1 font-display text-lg font-bold tracking-tight text-ink"
                  >
                    {step === 'profil' && 'Quel profil correspond à votre situation ?'}
                    {step === 'commune' && 'Votre commune (pour affiner les aides locales)'}
                    {step === 'done' && 'Votre espace est prêt.'}
                  </h2>
                  {step === 'profil' && (
                    <p className="mt-1.5 text-sm text-stone-600">
                      Le site s'adapte à votre besoin, changeable à tout moment depuis la
                      navigation.
                    </p>
                  )}
                  {step === 'commune' && (
                    <p className="mt-1.5 text-sm text-stone-600">
                      Optionnel — permet d'afficher les aides régionales et artisans de proximité.
                    </p>
                  )}
                </div>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={handleClose}
                aria-label="Fermer la fenêtre"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-stone-200 bg-paper text-stone-500 transition-colors duration-150 ease-out hover:bg-stone-50 hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {(step === 'profil' || step === 'commune') && (
              <div className="flex h-0.5 bg-stone-100">
                <motion.div
                  className="h-full bg-brand-500"
                  initial={{ width: '0%' }}
                  animate={{ width: step === 'profil' ? '50%' : '100%' }}
                  transition={{ duration: 0.3, ease: easeStripe }}
                />
              </div>
            )}

            <AnimatePresence mode="wait">
              {step === 'profil' && (
                <motion.div
                  key="profil"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.2, ease: easeStripe }}
                  className="grid gap-2 p-4 sm:grid-cols-2 sm:p-5"
                >
                  {AUDIENCES.map((a) => {
                    const Icon = AUDIENCE_ICON_MAP[a.iconName];
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => handleSelectAudience(a)}
                        className={cn(
                          'group relative flex items-start gap-3 rounded-xl border border-stone-200/70 bg-paper p-4 text-left transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated',
                        )}
                      >
                        {a.highlight && (
                          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-ink px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-ivory-50">
                            <span className="h-1 w-1 animate-glow-pulse rounded-full bg-accent-400" />
                            Phare
                          </span>
                        )}
                        <div
                          className={cn(
                            'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-150 ease-out group-hover:scale-105',
                            TONE_BG[a.tone],
                            TONE_TEXT[a.tone],
                          )}
                        >
                          <Icon className="h-4 w-4" strokeWidth={1.7} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-[15px] font-semibold text-ink">
                            {a.label}
                          </p>
                          <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
                            {a.tagline}
                          </p>
                        </div>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 self-center text-stone-300 opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                    );
                  })}
                </motion.div>
              )}

              {step === 'commune' && selected && (
                <motion.div
                  key="commune"
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.2, ease: easeStripe }}
                  className="p-5 sm:p-6"
                >
                  <div
                    className={cn(
                      'mb-5 flex items-center gap-3 rounded-xl border p-3.5 ring-1 ring-inset',
                      TONE_BG[selected.tone].split(' ')[0],
                      TONE_RING[selected.tone],
                    )}
                  >
                    {(() => {
                      const Icon = AUDIENCE_ICON_MAP[selected.iconName];
                      return (
                        <Icon
                          className={cn('h-5 w-5 shrink-0', TONE_TEXT[selected.tone])}
                          strokeWidth={1.7}
                        />
                      );
                    })()}
                    <span className={cn('text-sm font-semibold', TONE_TEXT[selected.tone])}>
                      Profil {selected.label} sélectionné
                    </span>
                    <CheckCircle2
                      className={cn('ml-auto h-4 w-4 shrink-0', TONE_TEXT[selected.tone])}
                      strokeWidth={1.8}
                    />
                  </div>

                  <div className="mb-5">
                    <label
                      htmlFor="audience-modal-commune"
                      className="mb-2 block text-sm font-medium text-ink"
                    >
                      Votre commune <span className="font-normal text-stone-400">(optionnel)</span>
                    </label>
                    <CitySelect
                      inputId="audience-modal-commune"
                      value={commune}
                      onChange={setCommune}
                    />

                    {/* Badges de zone climatique + Anah dès qu'une commune est sélectionnée */}
                    {commune &&
                      (() => {
                        const z = getZoning(commune.codeDepartement);
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, ease: easeStripe }}
                            className="mt-2.5 flex flex-wrap items-center gap-1.5"
                          >
                            <Thermometer className="h-3 w-3 text-stone-400" strokeWidth={1.5} />
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold',
                                CLIMATE_ZONE_COLORS[z.climateZone],
                              )}
                            >
                              {CLIMATE_ZONE_LABELS[z.climateZone]}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10.5px] font-medium text-stone-600">
                              {ANAH_ZONE_LABELS[z.anahZone]}
                            </span>
                            <span className="text-[10px] text-stone-400">
                              — barème d'aides calculé
                            </span>
                          </motion.div>
                        );
                      })()}

                    <p className="mt-2 flex items-start gap-1.5 text-[11px] text-stone-400">
                      <MapPin className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.5} />
                      Utilisée uniquement pour afficher les aides régionales et les artisans proches
                      de chez vous. Non partagée.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      onClick={handleSkipCommune}
                      className="order-2 sm:order-1"
                    >
                      Passer cette étape
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="lg"
                      onClick={handleConfirmCommune}
                      className="order-1 sm:order-2"
                    >
                      {commune ? 'Confirmer' : 'Continuer'}
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 'done' && selected && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.25, ease: easeStripe }}
                  className="flex flex-col items-center px-6 py-10 text-center"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 ring-8 ring-brand-50/50">
                    <CheckCircle2 className="h-7 w-7 text-brand-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-ink">
                    Profil&nbsp;
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[15px] ring-1 ring-inset',
                        TONE_BG[selected.tone].split(' ')[0],
                        TONE_TEXT[selected.tone],
                        TONE_RING[selected.tone],
                      )}
                    >
                      {(() => {
                        const Icon = AUDIENCE_ICON_MAP[selected.iconName];
                        return <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />;
                      })()}
                      {selected.label}
                    </span>
                    &nbsp;activé
                  </h3>
                  {commune &&
                    (() => {
                      const z = getZoning(commune.codeDepartement);
                      return (
                        <div className="mt-2 flex flex-col items-center gap-1.5">
                          <p className="flex items-center gap-1 text-[13px] text-stone-500">
                            <MapPin className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.5} />
                            {commune.nom} ({commune.codePostal}) — aides locales activées
                          </p>
                          <div className="flex items-center gap-1.5">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold',
                                CLIMATE_ZONE_COLORS[z.climateZone],
                              )}
                            >
                              {CLIMATE_ZONE_LABELS[z.climateZone]}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-600">
                              {ANAH_ZONE_LABELS[z.anahZone]}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  <p className="mt-2 text-[13px] leading-relaxed text-stone-600">
                    Le contenu et les outils s'adaptent à votre profil.
                    <br />
                    Changez à tout moment via le bouton en haut à droite.
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <a
                      href={selected.href}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[13px] font-semibold shadow-[0_2px_8px_rgba(0,0,0,.06)] transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_4px_16px_rgba(0,0,0,.08)]',
                        TONE_BG[selected.tone].split(' ')[0],
                        TONE_TEXT[selected.tone],
                        'ring-1 ring-inset',
                        TONE_RING[selected.tone],
                      )}
                    >
                      Voir mon espace {selected.shortLabel}
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </a>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex items-center rounded-xl border border-stone-200 bg-white px-5 py-2.5 text-[13px] font-medium text-stone-600 transition-colors hover:bg-stone-50 hover:text-ink"
                    >
                      Continuer la navigation
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {step === 'profil' && (
              <div className="flex items-center justify-between gap-3 border-t border-stone-200/70 bg-stone-50/50 px-6 py-3.5">
                <p className="text-xs text-stone-500">Gratuit, sans inscription, sans engagement</p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-xs font-medium text-stone-600 transition-colors hover:text-ink"
                >
                  Continuer sans profil →
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
